import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import ReactPaginate from "react-paginate";
import { useTranslation } from "react-i18next";
import { useRolePermission } from "../../../Hooks/useRolePermission";
import { useDispatch, useSelector } from "react-redux";
import MaterialSelect from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
// component
import ButtonElement from "../../UI/Button";
import { getTableSkeletons } from "../../UI/Skeleton/Loader";
// helper
import { getLocalStorage } from "../../../Network/ApiService";
import { ls_vars } from "../../../Library/Constants";
import { pageOptions } from "../../../StaticData/UserManageMent/status";
import { scrollBar } from "../../Common/Header/ManageOutsideClickClose";
import { handleError } from "../../../Library/Utils";
// API

import {
  getCountryUomList,
  deleteCountryUom,
} from "../../../Network/Core/CountryUom/CountryUom";

// redux
import { setSearch } from "../../../Store/Actions/EquipmentMaster/equipmentMasterActionCreatore";
import { setLoader } from "../../../Store/Actions/Loader/LoaderActionCreator";
// images
import images from "../../../Assets/images";
// css
import "../../../Assets/css/customize.css";
import "./style.css";
import { useApiCall } from "Hooks/useApiCall";
import {
  CountryUomType,
  GetCountryUomListParams,
} from "Network/Core/CountryUom/types";
import debounce from "lodash/debounce";
import CountryUomModal from "Components/Common/Modal/CountryUomModal";
import DeleteConfirmationModal from "Components/Common/Modal/DeleteConfirmationModal";
import Pagination from "Components/UI/Pagination/Pagination";
import { getFilteredCommonUtils } from "Network/Core/CommonUtils/commonUtils";
import { set } from "lodash";

const Index = () => {
  const { t } = useTranslation();
  const { call } = useApiCall();
  const { checkPermission } = useRolePermission();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const project = getLocalStorage(ls_vars.PROJECT_DATA);
  const region = getLocalStorage(ls_vars.REGION_ID);
  const dateFormate = getLocalStorage(ls_vars.USER_PREFERENCES)?.dateFormat?.id;

  const isLoading = useSelector((state: any) => state.loader.loading);
  const [orderBy, setOrderBy] = useState<string>("country");
  const [CountryuomData, setCountryuomData] = useState<CountryUomType[]>([]);
  const [statusIsASC, setStatusIsASC] = useState<boolean>(true);
  const [searchValue, setSearchValue] = useState<string>("");
  const [pageCount, setPageCount] = useState<number>(0);
  const [page, setPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<number>(10);
  const [isScrolling, setIsScrolling] = useState<boolean>(false);
  const [isCountryUomModalOpen, setIsCountryUomModalOpen] =
    useState<boolean>(false);
  const [isConfirmModalShow, setIsConfirmModalShow] = useState<boolean>(false);
  const [existingData, setExistingData] = useState<CountryUomType>(
    {} as CountryUomType
  );
  const [CountryUomToBeDeleted, setCountryUomToBeDeleted] = useState<any>();
  const [CountryOptions, setCountryOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [DistanceOptions, setDistanceOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [VolumeOptions, setVolumeOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [WeightOptions, setWeightOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [TonsOptions, setTonsOptions] = useState<
    { label: string; value: string }[]
  >([]);
  
  const canViewProjectSettings = checkPermission(["project:settings:view"]);
  const canCreateProjectSettings = checkPermission(["project:settings:create"]);

  scrollBar(setIsScrolling);

  // Handle Page Click
  const handlePageClick = (data: { selected: number }) => {
    dispatch(setLoader(true));
    const { selected } = data;
    setPage(selected + 1);
  };

  const onChangePage = (e: { value: number }) => {
    setPerPage(e.value);
  };

  // Handle Search Change with Debouncing
  const debouncedSearchValue = useRef(
    debounce((value: string) => {
      dispatch(setSearch(value));
    }, 500)
  ).current;

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchValue(value);
    debouncedSearchValue(value);
  };

  // Fetch CountryUom List
  const getCountryUomDetails = useCallback(async () => {
    dispatch(setLoader(true));

    try {
      let localPageCount = page;
      if (searchValue && page !== 1) {
        localPageCount = 1;
        setPage(1);
      }

      const params: GetCountryUomListParams = {
        page: localPageCount,
        pageSize: perPage,
        search: searchValue,
        orderBy: orderBy,
      };

      const res = await getCountryUomList(params, region);
      const { status, data, statusText } = res;
      switch (status) {
        case 200:
          setCountryuomData(data?.results);
          setPageCount(Math.ceil(data.count / perPage));
          break;
        case 400:
          handleError(data, t);
          break;
        default:
          toast.error(statusText);
          break;
      }
    } catch (err) {
      toast.error(t("Translation.error_something_went_wrong"));
    } finally {
      dispatch(setLoader(false));
    }
  }, [{ dispatch, page, perPage, orderBy, searchValue, t }]);

  useEffect(() => {
    getCountriesUom();
    getDistrictUom();
    getVolumeUom();
    getWeightUom();
    getTonsUom();
    getCountryUomDetails();
  }, [page, perPage, orderBy, searchValue]);

  const getCountriesUom = async () => {
    call(
      () => getFilteredCommonUtils("COUNTRY", region),
      (data) => {
        const options = data?.map((item: any) => ({
          label: item.name,
          value: item.id,
        }));
        const sortedOptions = options?.sort((a: any, b: any) =>
          a.label.localeCompare(b.label)
        );
        setCountryOptions(sortedOptions);
      }
    );
  };

  const getDistrictUom = async () => {
    call(
      () => getFilteredCommonUtils("DISTANCE", region),
      (data) => {
        const options = data?.map((item: any) => ({
          label: item.name,
          value: item.id,
        }));
        const sortedOptions = options?.sort((a: any, b: any) =>
          a.label.localeCompare(b.label)
        );
        setDistanceOptions(sortedOptions);
      }
    );
  };

  const getVolumeUom = async () => {
    call(
      () => getFilteredCommonUtils("VOLUME", region),
      (data) => {
        const options = data?.map((item: any) => ({
          label: item.name,
          value: item.id,
        }));
        const sortedOptions = options?.sort((a: any, b: any) =>
          a.label.localeCompare(b.label)
        );
        setVolumeOptions(sortedOptions);
      }
    );
  };

  const getWeightUom = async () => {
    call(
      () => getFilteredCommonUtils("WEIGHT", region),
      (data) => {
        const options = data?.map((item: any) => ({
          label: item.name,
          value: item.id,
        }));
        const sortedOptions = options?.sort((a: any, b: any) =>
          a.label.localeCompare(b.label)
        );
        setWeightOptions(sortedOptions);
      }
    );
  };

  const getTonsUom = async () => {
    call(
      () => getFilteredCommonUtils("TONS", region),
      (data) => {
        const options = data?.map((item: any) => ({
          label: item.name,
          value: item.id,
        }));
        const sortedOptions = options?.sort((a: any, b: any) =>
          a.label.localeCompare(b.label)
        );
        setTonsOptions(sortedOptions);
      }
    );
  };

  // Toggle Sorting
  const toggleSort = (field: string, isAcs: boolean) => {
    if (field !== "id") {
      setStatusIsASC(!isAcs);
      setOrderBy(isAcs ? field : "-" + field);
    }
  };

  // Open Add Equipment Modal
  const handleAddCountryUom = () => {
    setExistingData({} as CountryUomType);
    setIsCountryUomModalOpen(true);
  };

  // Close Modal and Refresh Data
  const handleCountryUomModalClose = () => {
    setIsCountryUomModalOpen(false);
    getCountryUomDetails();
  };

  // Delete CountryUom (logic for the confirmation modal)
  const handleDeleteCountryUom = async (CountryUom: any) => {
    if (!CountryUom || CountryUom.id === undefined) {
      console.error("Cannot delete CountryUom: Invalid ID");
      return;
    }
    call(
      () => deleteCountryUom(CountryUom.id, region),
      (res: any) => {
        closeConfirmationModal();
      },
      { successMessage: "Translation.country_uom_delete_success" }
    );
  };

  const closeConfirmationModal = () => {
    setIsConfirmModalShow(false);
    getCountryUomDetails();
  };
  return (
    <>
      {isCountryUomModalOpen && (
        <>
          <CountryUomModal
            handleClose={() => handleCountryUomModalClose()}
            existingData={existingData}
            Options={{
              country: { label: t("Translation.country"), options: CountryOptions },
              distance: { label: t("Translation.distance_uom"), options: DistanceOptions },
              volume: { label: t("Translation.volume_uom"), options: VolumeOptions },
              weight: { label: t("Translation.weight_uom"), options: WeightOptions },
              tons: { label: t("Translation.tons_uom"), options: TonsOptions }
            }}
          />
          <div className="modal-backdrop fade show" />
        </>
      )}

      {isConfirmModalShow && (
        <DeleteConfirmationModal
          isOpen={isConfirmModalShow}
          contentName={"CountryUom"}
          deleteContent={"CountryUomDelete"}
          closeModal={closeConfirmationModal}
          onConfirmAction={() => handleDeleteCountryUom(CountryUomToBeDeleted)}
        />
      )}

      <div className="page-wrapper pb-0">
        <div className="page-header">
          <h3 className="page-title">{t("Translation.country_uom")}</h3>
        </div>

        <div className="page-content custom-list-div">
          <div className="user-head-sec">
            <div className="booking-list-form">
              <div className="filter-wrapper">
                <div className="f-left">
                  <div className="form-group form-inline filter-search ml-0">
                    <img src={images.search} />
                    <input
                      type="text"
                      className="form-control"
                      placeholder={t("Translation.search")}
                      value={searchValue}
                      onChange={handleSearchChange}
                    />
                  </div>

                  {canCreateProjectSettings && (<ButtonElement
                    buttonLabel={t("Translation.country_uom_add")}
                    handleClick={handleAddCountryUom}
                    className="btn theme-btn invite-btn"
                  />)}
                </div>
              </div>
            </div>
          </div>

          <div className="booking-list-wrapper">
            <div
              className={`booking-list-data scroll ${
                isScrolling ? "show-scroll" : "no-scroll"
              }`}
            >
              <div className="tab-content" id="pills-tabContent">
                <div
                  className="tab-pane fade show active"
                  id="pills-all"
                  role="tabpanel"
                  aria-labelledby="pills-all-tab"
                >
                  <div className="table-responsive">
                    {isLoading ? (
                      getTableSkeletons({
                        cols: 6,
                        rows: 3,
                        props: {
                          TableRowProps: {
                            style: { height: "75px" },
                          },
                        },
                      })
                    ) : (
                      <table className="display nowrap dataTable w-100">
                        <thead>
                          <tr>
                            <th
                              className="sorting sorting_asc"
                              onClick={() => toggleSort("country__name", statusIsASC)}
                            >
                              <span>{t("Translation.country")}</span>
                            </th>
                            <th
                              className="sorting"
                              onClick={() =>
                                toggleSort("distance__name", statusIsASC)
                              }
                            >
                              <span>{t("Translation.distance_uom")}</span>
                            </th>
                            <th
                              className="sorting"
                              onClick={() => toggleSort("volume__name", statusIsASC)}
                            >
                              <span>{t("Translation.volume_uom")}</span>
                            </th>
                            <th
                              className="sorting"
                              onClick={() => toggleSort("weight__name", statusIsASC)}
                            >
                              <span>{t("Translation.weight_uom")}</span>
                            </th>
                            
                            <th
                              className="sorting"
                              onClick={() => toggleSort("tons__name", statusIsASC)}
                            >
                              <span>{t("Translation.tons_uom")}</span>
                            </th>
                            <th
                              className="sorting"
                              onClick={() => toggleSort("actions", statusIsASC)}
                            >
                              {t("Translation.actions")}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {CountryuomData.length > 0 ? (
                            CountryuomData.map((row, index) => (
                              row && (
                                <tr key={index}>
                                <td>{row.country?.name}</td>
                                <td>{row.distance?.name}</td>
                                <td>{row.volume?.name}</td>
                                <td>{row.weight?.name}</td>
                                <td>{row.tons?.name}</td>
                                <td>
                                  <div className="action-buttons">
                                    {canCreateProjectSettings && (<button
                                      className="btn bg-transparent border-0"
                                      onClick={() => {
                                        setExistingData(row);
                                        setIsCountryUomModalOpen(true);
                                      }}
                                    >
                                      <img src={images.Pen} alt="edit" />
                                    </button>)}

                                    {canCreateProjectSettings && (<button
                                      className="btn bg-transparent border-0"
                                      onClick={() => {
                                        setCountryUomToBeDeleted(row);
                                        setIsConfirmModalShow(true);
                                      }}
                                    >
                                      <img src={images.DeleteRedIcon} />
                                    </button>)}
                                  </div>
                                </td>
                              </tr>
                              )                              
                            ))
                          ) : (
                            <tr>
                              <td colSpan={4} className="text-center">
                                {t("Translation.no_records_found")}
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {CountryuomData.length > 0 ? (
              <Pagination
                page={page}
                onPageChange={(v) => setPage(v)}
                perPage={perPage}
                onPerPageChange={({ value }) => setPerPage(value)}
                pageTotalCount={pageCount}
              />
            ) : (
              ""
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Index;
